import { io } from 'socket.io-client';
import { auth } from '../config/firebase';
import { API_BASE_URL } from '../config/apiBaseUrl';

let socketInstance = null;
let connectPromise = null;

const isAuthConnectError = (error) => {
  const message = String(error?.message ?? '').toLowerCase();
  return (
    message.includes('unauthorized') ||
    message.includes('forbidden') ||
    message.includes('jwt') ||
    message.includes('token') ||
    message.includes('auth')
  );
};

export const getSocketServerUrl = () => API_BASE_URL;

export const getFirebaseSocketAuthToken = async ({ forceRefresh = false } = {}) => {
  await auth.authStateReady();
  const user = auth.currentUser;
  if (!user) return null;

  try {
    return await user.getIdToken(forceRefresh);
  } catch (error) {
    console.error('[Socket] Failed to read Firebase ID token:', error);
    return null;
  }
};

export const getSocketClient = () => socketInstance;

export const connectSocketClient = async () => {
  if (socketInstance?.connected) {
    return socketInstance;
  }

  if (connectPromise) {
    return connectPromise;
  }

  connectPromise = (async () => {
    const token = await getFirebaseSocketAuthToken();
    if (!token) {
      return null;
    }

    if (socketInstance) {
      socketInstance.removeAllListeners();
      socketInstance.disconnect();
      socketInstance = null;
    }

    const socket = io(getSocketServerUrl(), {
      auth: { token },
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => {
      if (process.env.NODE_ENV !== 'production') {
        console.info('[Socket] Connected', socket.id);
      }
    });

    socket.on('connect_error', async (error) => {
      console.error('[Socket] Connection failed:', error?.message || error);

      if (!isAuthConnectError(error)) {
        return;
      }

      const refreshedToken = await getFirebaseSocketAuthToken({ forceRefresh: true });
      if (!refreshedToken || socketInstance !== socket) {
        return;
      }

      socket.auth = { token: refreshedToken };
      if (!socket.connected) {
        socket.connect();
      }
    });

    socketInstance = socket;
    return socket;
  })();

  try {
    return await connectPromise;
  } finally {
    connectPromise = null;
  }
};

export const disconnectSocketClient = () => {
  connectPromise = null;

  if (!socketInstance) {
    return;
  }

  socketInstance.removeAllListeners();
  socketInstance.disconnect();
  socketInstance = null;
};
